import axios from "axios";
import { MLProcessType as MLProcessTypeCommon, MLRunStatus, MLRun as MLRunCommon } from "@bageltechnologies/bagel-common";

// Interfaces
import { AccountExploreOptions } from '../utils/exploreAccess';

export interface AccountSettings {
  _id: string;
  accountId: string;
  domains?: string[];
  options?: AccountExploreOptions;
}

export type PIIEmailReductionType = 'redact' | 'mask';
export type PIIMaskStyle = 'first_letter_and_domain';

export interface PIITypeExtraSettings {
  reductionType: PIIEmailReductionType;
  maskStyle?: PIIMaskStyle;
}

export interface PIIConfiguration {
  type: 'configuration';
  name: 'pii-redaction';
  isEnabled: boolean;
  selectedTypes: string[];
  excludedDomains: string[];
  extraSettings?: Partial<Record<string, PIITypeExtraSettings>>;
  updatedAt: Date;
  createdAt: Date;
}

export interface PIIInfoType {
  name: string;
  label?: string;
}

export interface PIIInfoTypeGroup {
  id: string;
  label: string;
  description?: string;
  types: PIIInfoType[];
}

export interface PIIConfigurationResponse {
  config: PIIConfiguration;
  availableTypes: PIIInfoType[];
  availableTypeGroups: PIIInfoTypeGroup[];
}

export interface MLSettings {
  businessCategoryDaysWindow: { [businessCategoryKey: string]: number };
}

export interface PACModelConfig {
  _id: {
    $oid: string;
  };
  name: string;
  account_id: {
    $oid: string;
  };
  auto_recalc: boolean;
  target_model: {
    model_name: string;
    model_version: string;
    [key: string]: any;
  };
  tuning_needed: boolean;
  updated_at: string;
  last_completed_recalc: {
    finished_at: string;
    model: {
      model_name: string;
      model_version: string;
      [key: string]: any;
    };
    [key: string]: any;
  };
  [key: string]: any;
}

export interface IgnoreAccountIdsConfiguration {
  isEnabled: boolean;
  accountIds: string[];
}

export interface TokenDocument {
  _id: string;
  token: string;
  name: string;
  createdAt: string;
  username: string;
}

// Dev Team Interface
export interface DevTeam {
  _id: string;
  name: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// User Invitation
export interface UserInvite {
  name: string;
  email: string;
  role?: string;
  team?: string;
}

export interface InviteOptions {
  defaultRole?: string;
  sendInvite?: boolean;
}

const API_BASE_URL = window.__RUNTIME_CONFIG__.REACT_APP_API_DOMAIN + "/settings";

// Account Settings API
export const getAccountSettings = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/`);
    return response.data.account;
  } catch (e) {
    console.error("Error fetching account settings", e);
    throw e;
  }
};

export const updateAccountSettings = async (settings: Partial<AccountSettings>, auth0: any) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/`,
      { settings });
    return response.data;
  } catch (e) {
    console.error("Error updating account settings", e);
    throw e;
  }
};

export const updateDomains = async (domain: string, push: boolean, auth0: any) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/domains`,
      { data: { domain, push } });
    return response.data;
  } catch (e) {
    console.error("Error updating domains", e);
    throw e;
  }
};

// API Tokens
export const createToken = async (type: string, name: string, auth0: any) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/token/${type}`,
      { name });
    return response.data;
  } catch (e) {
    console.error("Error creating token", e);
    throw e;
  }
};

export const getTokens = async (type: string, auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/token/${type}`);
    return response.data.tokenDoc;
  } catch (e) {
    console.error("Error fetching tokens", e);
    throw e;
  }
};

export const deleteToken = async (id: string, auth0: any) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/token/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting token", e);
    throw e;
  }
};

// PII Redaction
export const getPIIConfiguration = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/pii-redaction`);
    return response.data;
  } catch (e) {
    console.error("Error fetching PII configuration", e);
    throw e;
  }
};

export const updatePIIConfiguration = async (
  update: {
    isEnabled: boolean;
    selectedTypes: string[];
    excludedDomains: string[];
    extraSettings?: Partial<Record<string, PIITypeExtraSettings>>;
  },
  auth0: any
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/pii-redaction`,
      update);
    return response.data;
  } catch (e) {
    console.error("Error updating PII configuration", e);
    throw e;
  }
};

// Dev Teams API
export const getDevTeams = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/dev-teams`);
    return response.data;
  } catch (e) {
    console.error("Error fetching dev teams", e);
    throw e;
  }
};

export const getDevTeam = async (id: string, auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/dev-teams/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error fetching dev team", e);
    throw e;
  }
};

export const createDevTeam = async (
  team: { name: string; description?: string; ownerId: string; ownerName: string },
  auth0: any
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/dev-teams`,
      team);
    return response.data;
  } catch (e) {
    console.error("Error creating dev team", e);
    throw e;
  }
};

export const updateDevTeam = async (
  id: string,
  update: { name?: string; description?: string; ownerId?:string },
  auth0: any
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/dev-teams/${id}`,
      update);
    return response.data;
  } catch (e) {
    console.error("Error updating dev team", e);
    throw e;
  }
};

export const deleteDevTeam = async (id: string, auth0: any) => {
  try {
    const response = await axios.delete(`${API_BASE_URL}/dev-teams/${id}`);
    return response.data;
  } catch (e) {
    console.error("Error deleting dev team", e);
    throw e;
  }
};

// User Invitation
export const inviteBulkUsers = async (
  users: UserInvite[],
  options: InviteOptions = {},
  auth0: any
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/invite-users`,
      { users, options });
    return response.data;
  } catch (e) {
    console.error("Error inviting users", e);
    throw e;
  }
}; 


// Bagel Objects
export const getBagelObjects = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/bagel-objects`);
    return response.data.bagelObjects;
  } catch (e) {
    console.error("Error fetching Bagel objects", e);
    throw e;
  }
};

// Get sources for a bagel object
export const getObjectSources = async (bagelObjectKey: string, auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/bagel-objects/${bagelObjectKey}/sources`);
    return response.data.sources;
  } catch (e) {
    console.error("Error fetching object sources", e);
    throw e;
  }
};

// Get a single object source by sourceId
export const getObjectSource = async (bagelObjectKey: string, sourceId: string, auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/bagel-objects/${bagelObjectKey}/sources/${sourceId}`);
    return response.data.source;
  } catch (e) {
    console.error("Error fetching object source", e);
    throw e;
  }
};

// Get CSV rows for a CSV source
export const getCsvSourceRows = async (bagelObjectKey: string, sourceId: string, auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/bagel-objects/${bagelObjectKey}/sources/${sourceId}/csv-rows`);
    return response.data;
  } catch (e) {
    console.error("Error fetching CSV source rows", e);
    throw e;
  }
};

// Create a new object source
export interface CreateObjectSourceRequest {
  origin: {
    integrationKey: string;   // e.g., 'salesforce', 'csv'
    originObject: string;     // e.g., 'Case', 'requests'
  };
  advanced?: {
    startDate?: string;
    filters?: any;
  };
  fieldMappings: Record<string, { originField: string; fieldType?: string; type?: string; value?: string }>;
  csvRecords?: Array<Record<string, any>>;  // Only for CSV origin
}

export const createObjectSource = async (
  bagelObjectKey: string,
  data: CreateObjectSourceRequest,
  auth0: any
) => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}/bagel-objects/${bagelObjectKey}/sources`,
      data);
    return response.data;
  } catch (e) {
    console.error("Error creating object source", e);
    throw e;
  }
};

// Update an existing object source
export interface UpdateObjectSourceRequest {
  advanced?: {
    startDate?: string;
    filters?: any;
  };
  fieldMappings: Record<string, { originField: string; fieldType?: string; type?: string; value?: string }>;
  csvRecords?: Array<Record<string, any>>;  // Only for CSV origin
}

export const updateObjectSource = async (
  bagelObjectKey: string,
  sourceId: string,
  data: UpdateObjectSourceRequest,
  auth0: any
) => {
  try {
    const response = await axios.patch(
      `${API_BASE_URL}/bagel-objects/${bagelObjectKey}/sources/${sourceId}`,
      data);
    return response.data;
  } catch (e) {
    console.error("Error updating object source", e);
    throw e;
  }
};

// Ignore Account IDs
export const getIgnoreAccountIds = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/ignore-account-ids`);
    return response.data.config;
  } catch (e) {
    console.error("Error fetching Ignore Account IDs configuration", e);
    throw e;
  }
};

export const updateIgnoreAccountIds = async (
  update: {
    isEnabled?: boolean;
    accountIds?: string[];
  },
  auth0: any
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/ignore-account-ids`,
      update);
    return response.data;
  } catch (e) {
    console.error("Error updating Ignore Account IDs configuration", e);
    throw e;
  }
};

// ML Settings
export const getMLSettings = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/ml`);
    return response.data.config;
  } catch (e) {
    console.error("Error fetching ML settings", e);
    throw e;
  }
};

export const updateMLSettings = async (
  update: {
    businessCategoryDaysWindow: { [businessCategoryKey: string]: number };
  },
  auth0: any
) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/ml`,
      update);
    return response.data;
  } catch (e) {
    console.error("Error updating ML settings", e);
    throw e;
  }
};

// PACModelConfig Settings
export const getPACModelConfig = async (auth0: any) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/ml/PACModelConfig`);
    return response.data;
  } catch (e) {
    console.error("Error fetching PACModelConfig", e);
    throw e;
  }
};

export const triggerPACModelRecalculation = async (auth0: any) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/ml/recalc-all-product-areas`, {});
    return response.data;
  } catch (e) {
    console.error("Error triggering PACModelConfig recalculation", e);
    throw e;
  }
};

// ML Process Types
export type MLFlowName = 'pac_recalc_all' | 'matching' | 'nightly_ops' | 'generate_ideas' | string;

export interface MLProcessResponse {
  success: boolean;
  message?: string;
  [key: string]: any;
}

// ML Process Type - frontend version (using string IDs)
export interface MLProcessType extends Omit<MLProcessTypeCommon, '_id'> {
  _id: string;
}

// ML Run Status - re-export from bagel-common
export type { MLRunStatus };

// ML Run - frontend version (using string IDs and date strings)
export interface MLRun extends Omit<MLRunCommon, '_id' | 'accountId' | 'waitingAt' | 'pendingAt' | 'startedAt' | 'updatedAt' | 'completedAt'> {
  _id: string;
  waitingAt?: string;
  pendingAt?: string;
  startedAt?: string;
  updatedAt?: string;
  completedAt?: string;
}

// ML Run History Response
export interface MLRunHistoryResponse {
  runs: MLRun[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// Get ML Process Types
export const getMLProcessTypes = async (auth0: any): Promise<MLProcessType[]> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/ml/process-types`);
    return response.data.processTypes;
  } catch (e) {
    console.error("Error fetching ML process types", e);
    throw e;
  }
};

// Get ML Run History for a specific flow
export const getMLRunHistory = async (
  auth0: any,
  flowName: string,
  page = 1,
  limit = 20
): Promise<MLRunHistoryResponse> => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/ml/runs/${flowName}?page=${page}&limit=${limit}`);
    return response.data;
  } catch (e) {
    console.error(`Error fetching ML run history for ${flowName}`, e);
    throw e;
  }
};

// Trigger ML Process - calls ML API directly
const ML_API_BASE_URL = window.__RUNTIME_CONFIG__.REACT_APP_MLAPI_DOMAIN;

export const triggerMLProcess = async (auth0: any, flowName: MLFlowName): Promise<MLProcessResponse> => {
  try {
    const response = await axios.post(
      `${ML_API_BASE_URL}/ml/run_process`,
      { flowName });
    return response.data;
  } catch (e) {
    console.error(`Error triggering ML process: ${flowName}`, e);
    throw e;
  }
};

